import { motion } from "framer-motion";
import { ArrowLeft, Shield, Lock, Eye, Database, Cookie, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export default function PolitiqueConfidentialite() {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <motion.div {...fadeInUp}>
          <Button
            variant="ghost"
            className="mb-8"
            asChild
          >
            <a href="/">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Retour à l'accueil
            </a>
          </Button>

          <div className="flex items-center gap-4 mb-8">
            <div className="p-4 bg-primary/10 rounded-2xl">
              <Shield className="w-12 h-12 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-black">Politique de Confidentialité</h1>
              <p className="text-muted-foreground mt-2">Dernière mise à jour : 5 avril 2026</p>
            </div>
          </div>

          <Card className="p-8 mb-8 bg-primary/5 border-primary/20">
            <p className="text-lg leading-relaxed">
              VEHORIA (VEHORIA ACADEMY), représentée par Camille Vega De Hoyos, accorde une grande importance à la protection de vos données personnelles. Cette politique de confidentialité vous informe sur la manière dont nous collectons, utilisons et protégeons vos informations conformément au Règlement Général sur la Protection des Données (RGPD).
            </p>
          </Card>

          <div className="space-y-6">
            <Card className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <Database className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-2">1. Données collectées</h2>
                </div>
              </div>
              <div className="space-y-4 text-muted-foreground">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Données d'identification</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Nom et prénom</li>
                    <li>Adresse email</li>
                    <li>Numéro de téléphone</li>
                    <li>Adresse postale (si nécessaire pour la livraison)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Données de navigation</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Adresse IP</li>
                    <li>Type de navigateur</li>
                    <li>Pages visitées</li>
                    <li>Durée de visite</li>
                    <li>Données de cookies (avec votre consentement)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Données de commande</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Historique des achats</li>
                    <li>Informations de paiement (traitées de manière sécurisée par Stripe)</li>
                    <li>Préférences de formation</li>
                  </ul>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-secondary/10 rounded-xl">
                  <Eye className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-2">2. Finalités du traitement</h2>
                </div>
              </div>
              <div className="space-y-3 text-muted-foreground">
                <p>Vos données personnelles sont collectées et traitées pour les finalités suivantes :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">Gestion des commandes :</strong> Traitement, livraison et suivi de vos formations</li>
                  <li><strong className="text-foreground">Communication :</strong> Réponse à vos demandes de contact et support client</li>
                  <li><strong className="text-foreground">Marketing :</strong> Envoi de newsletters et offres promotionnelles (avec votre consentement)</li>
                  <li><strong className="text-foreground">Amélioration du service :</strong> Analyse statistique et amélioration de l'expérience utilisateur</li>
                  <li><strong className="text-foreground">Obligations légales :</strong> Respect des obligations comptables et fiscales</li>
                  <li><strong className="text-foreground">Sécurité :</strong> Prévention de la fraude et protection du site</li>
                </ul>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <Lock className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-2">3. Base légale du traitement</h2>
                </div>
              </div>
              <div className="space-y-3 text-muted-foreground">
                <p>Le traitement de vos données repose sur les bases légales suivantes :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">Exécution du contrat :</strong> Traitement de vos commandes et livraison des formations</li>
                  <li><strong className="text-foreground">Consentement :</strong> Cookies non essentiels, newsletters et communications marketing</li>
                  <li><strong className="text-foreground">Intérêt légitime :</strong> Amélioration de nos services et sécurité du site</li>
                  <li><strong className="text-foreground">Obligation légale :</strong> Conservation des données comptables et fiscales</li>
                </ul>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-secondary/10 rounded-xl">
                  <Cookie className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-2">4. Cookies et technologies similaires</h2>
                </div>
              </div>
              <div className="space-y-4 text-muted-foreground">
                <p>Notre site utilise des cookies pour améliorer votre expérience de navigation.</p>
                
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Cookies essentiels (obligatoires)</h3>
                  <p>Ces cookies sont nécessaires au fonctionnement du site et ne peuvent pas être désactivés :</p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                    <li>Cookies de session</li>
                    <li>Cookies de sécurité</li>
                    <li>Cookies de préférence de consentement</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">Cookies analytiques (optionnels)</h3>
                  <p>Ces cookies nous aident à comprendre comment vous utilisez notre site :</p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                    <li>Google Analytics (si accepté)</li>
                    <li>Statistiques de visite</li>
                    <li>Analyse du comportement utilisateur</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">Cookies marketing (optionnels)</h3>
                  <p>Ces cookies permettent de personnaliser les publicités :</p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                    <li>Publicités ciblées</li>
                    <li>Réseaux sociaux (Instagram, LinkedIn)</li>
                    <li>Remarketing</li>
                  </ul>
                </div>

                <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                  <p className="font-semibold text-foreground">Gestion de vos préférences</p>
                  <p className="mt-2">Vous pouvez à tout moment modifier vos préférences de cookies en cliquant sur le lien "Gérer les cookies" en bas de page ou en accédant aux paramètres de votre navigateur.</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <UserCheck className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-2">5. Vos droits</h2>
                </div>
              </div>
              <div className="space-y-3 text-muted-foreground">
                <p>Conformément au RGPD, vous disposez des droits suivants :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">Droit d'accès :</strong> Obtenir une copie de vos données personnelles</li>
                  <li><strong className="text-foreground">Droit de rectification :</strong> Corriger vos données inexactes ou incomplètes</li>
                  <li><strong className="text-foreground">Droit à l'effacement :</strong> Demander la suppression de vos données</li>
                  <li><strong className="text-foreground">Droit à la limitation :</strong> Limiter le traitement de vos données</li>
                  <li><strong className="text-foreground">Droit à la portabilité :</strong> Recevoir vos données dans un format structuré</li>
                  <li><strong className="text-foreground">Droit d'opposition :</strong> Vous opposer au traitement de vos données</li>
                  <li><strong className="text-foreground">Droit de retirer votre consentement :</strong> À tout moment pour les traitements basés sur le consentement</li>
                </ul>
                
                <div className="bg-secondary/5 p-4 rounded-lg border border-secondary/20 mt-4">
                  <p className="font-semibold text-foreground mb-2">Comment exercer vos droits ?</p>
                  <p>Pour exercer vos droits, contactez-nous :</p>
                  <ul className="list-none space-y-1 mt-2">
                    <li>📧 Email : <a href="mailto:contact@lorishug.fr" className="text-primary hover:underline">contact@lorishug.fr</a></li>
                    <li>📞 Téléphone : +33 6 04 41 76 82</li>
                    <li>✉️ Courrier : 60 rue François Ier, 75008 PARIS</li>
                  </ul>
                  <p className="mt-2 text-sm">Nous nous engageons à répondre dans un délai d'un mois maximum.</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">6. Durée de conservation</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>Vos données sont conservées pendant les durées suivantes :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">Données de compte :</strong> Jusqu'à la suppression de votre compte + 1 an</li>
                  <li><strong className="text-foreground">Données de commande :</strong> 10 ans (obligations comptables)</li>
                  <li><strong className="text-foreground">Données de prospection :</strong> 3 ans à compter du dernier contact</li>
                  <li><strong className="text-foreground">Cookies :</strong> 13 mois maximum</li>
                  <li><strong className="text-foreground">Données de navigation :</strong> 25 mois (Google Analytics)</li>
                </ul>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">7. Sécurité des données</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Chiffrement SSL/TLS pour toutes les communications</li>
                  <li>Hébergement sécurisé (Vercel)</li>
                  <li>Paiement sécurisé via Stripe (certifié PCI-DSS)</li>
                  <li>Accès restreint aux données personnelles</li>
                  <li>Sauvegardes régulières</li>
                  <li>Surveillance et détection des intrusions</li>
                </ul>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">8. Partage des données</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>Vos données peuvent être partagées avec :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">Prestataires de services :</strong> Stripe (paiement), Vercel (hébergement)</li>
                  <li><strong className="text-foreground">Autorités légales :</strong> En cas d'obligation légale ou de demande judiciaire</li>
                </ul>
                <p className="mt-4">Nous ne vendons jamais vos données personnelles à des tiers.</p>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">9. Transferts internationaux</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>Certains de nos prestataires peuvent être situés en dehors de l'Union Européenne :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">Vercel (USA) :</strong> Clauses contractuelles types de la Commission Européenne</li>
                  <li><strong className="text-foreground">Stripe (USA) :</strong> Certifié Privacy Shield et clauses contractuelles types</li>
                </ul>
                <p className="mt-4">Ces transferts sont encadrés par des garanties appropriées conformément au RGPD.</p>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">10. Modifications de la politique</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. Toute modification sera publiée sur cette page avec une nouvelle date de mise à jour.</p>
                <p>En cas de modification substantielle, nous vous en informerons par email ou via une notification sur le site.</p>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">11. Réclamation</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la CNIL :</p>
                <div className="bg-muted/50 p-4 rounded-lg mt-4">
                  <p className="font-semibold text-foreground">Commission Nationale de l'Informatique et des Libertés (CNIL)</p>
                  <p className="mt-2">3 Place de Fontenoy - TSA 80715</p>
                  <p>75334 PARIS CEDEX 07</p>
                  <p className="mt-2">Téléphone : 01 53 73 22 22</p>
                  <p>Site web : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.cnil.fr</a></p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-primary/5 border-primary/20">
              <h2 className="text-2xl font-bold mb-4">Contact</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>Pour toute question concernant cette politique de confidentialité ou l'exercice de vos droits :</p>
                <div className="mt-4 space-y-2">
                  <p><strong className="text-foreground">VEHORIA (VEHORIA ACADEMY)</strong></p>
                  <p>Directeur de la publication : Camille Vega De Hoyos</p>
                  <p>Responsable éditorial : Loris Hug</p>
                  <p>Email : <a href="mailto:contact@lorishug.fr" className="text-primary hover:underline">contact@lorishug.fr</a></p>
                  <p>Téléphone : +33 6 04 41 76 82</p>
                  <p>Adresse : 60 rue François Ier, 75008 PARIS</p>
                </div>
              </div>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <Button size="lg" asChild>
              <a href="/">
                <ArrowLeft className="mr-2 w-4 h-4" />
                Retour à l'accueil
              </a>
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
